import axios, {
  AxiosError,
  AxiosResponse,
  InternalAxiosRequestConfig,
} from 'axios';

import environment, { EnvironmentVariables } from '../utils/environment';
import TokenManager from './TokenManager';
import { auth } from './Endpoints';

const requestHandler = async (request: InternalAxiosRequestConfig) => {
  const token = TokenManager.getToken();
  const clientType = TokenManager.getClientType();
  const clientVersion = TokenManager.getClientVersion();
  if (token) {
    request.headers.cookie = token;
  }
  if (clientType) {
    request.headers['x-platform'] = clientType;
  }
  if (clientVersion) {
    request.headers['x-version'] = clientVersion;
  }
  return request;
};

const successHandler = async (
  response: AxiosResponse,
  request: InternalAxiosRequestConfig,
) => {
  const responseHeaders = response?.headers;
  if (responseHeaders) {
    const cookie = responseHeaders['set-cookie'];
    const authToken = responseHeaders['token'];
    if (cookie) {
      persistAuthCookie(cookie);
    }

    if (authToken) {
      TokenManager.setAuthToken(authToken);
    }
  }

  if (response.data.status === 'VM_UNAUTH') {
    if (response.config.url !== '/api/authenticate/refreshToken') {
      try {
        const refreshResponse = await auth.getRefreshToken();
        const newToken = refreshResponse.data.accessToken;
        TokenManager.setAuthToken(newToken);
        return axios(request);
      } catch {
        TokenManager.notifyListener(true);
      }
    } else {
      TokenManager.notifyListener(true);
    }
  }

  if (response.data.status !== 'VM_OK' || response.status !== 200) {
    return Promise.reject(response.data);
  }

  return response.data;
};

const persistAuthCookie = (cookie: string[]) => {
  const sanitizeString = cookie[0].split('; ');
  let finalCookie = '';
  for (const c of sanitizeString) {
    if (c.includes('authenticator=')) {
      finalCookie = `${c};`;
    }
  }
  if (finalCookie) {
    TokenManager.setToken(finalCookie);
  }
};

const errorHandler = (error: AxiosError | Error) => {
  let errorObj: ErrorData = {
    statusCode: -1,
    message: '',
    errorType: 'networkError',
  };

  const isDev =
    environment.get(EnvironmentVariables.ENVIRONMENT, 'false') === 'true';

  if (axios.isAxiosError(error) && error.response) {
    if (isDev) {
      console.log('Response Status:', error.response.status);
      console.log('Response Data:', error.response.data);
      console.log('Response Headers:', error.response.headers);
      console.log('Response Config:', error.config);
    }
    const isRawError = typeof error.response.data === 'string';
    errorObj = {
      statusCode: error.response.status,
      message: isRawError ? error.response.data : error.response.data.message,
      errorType: 'apiError',
    };
    if (error.response.status >= 500 && error.response.status < 599) {
      errorObj = {
        ...errorObj,
        message:
          'App is temporarily unable to connect to the Server. Please try again later.',
      };
    }
  } else {
    // Something else happened while setting up the request
    if (isDev) {
      console.log('Error Message:', error.message);
    }
    let message = error?.message;
    if (message.includes('timeout')) {
      message =
        'Looks like the server is taking to long to respond, this can be caused by either poor connectivity or an error with our servers. Please try again in a while.';
    }
    errorObj = { ...errorObj, message };
  }

  //Todo: Genric error cases should be handled here - ajith
  return Promise.reject(errorObj);
};

type ErrorData = {
  statusCode: number;
  message: string;
  errorType: 'networkError' | 'apiError';
};
export { requestHandler, successHandler, errorHandler };
